import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Check, Shield, Sparkles, Crown, ArrowRight, Minus, Plus as PlusIcon, ChevronDown, ChevronUp, X as XIcon } from "lucide-react";

const FAQ_ITEMS = [
  {
    q: "Can I switch plans later?",
    a: "Yes — move up or down between Starter, The Works, and Ultimate whenever your needs change. Upgrades take effect immediately with a prorated charge; downgrades apply at your next renewal so you never lose paid-up access.",
  },
  {
    q: "What happens at renewal?",
    a: "Your plan renews automatically on the same cadence (monthly or annual). We send a renewal notice seven days before the charge. If seat counts changed during the term, we true-up at renewal rather than mid-cycle so your bill stays predictable.",
  },
  {
    q: "Do you offer annual billing discounts?",
    a: "Yes. Switching from monthly to annual saves 50% on Starter and 33% on The Works per seat. Ultimate is annual-only and quoted per customer.",
  },
  {
    q: "Is there a free trial?",
    a: "We don't run a self-serve trial, but every purchase begins in Stripe test mode until our team provisions your tenant and you sign off on access. If the fit isn't right in the first 14 days, we cancel the live charge and part on good terms.",
  },
  {
    q: "What payment methods do you accept?",
    a: "All major cards (Visa, Mastercard, Amex) through Stripe, plus SEPA direct debit and ACH for annual deals above 10 seats. Invoicing with bank transfer is available on Ultimate.",
  },
  {
    q: "Can I cancel anytime?",
    a: "On monthly plans — yes, from your billing settings; cancellation takes effect at the end of the paid month. On annual plans you can switch off auto-renewal any time; the term runs to its natural end-date.",
  },
];

const PLAN_ICON = { starter: Shield, works: Sparkles, ultimate: Crown };
const PLAN_TAGLINE = {
  starter: "Run a disciplined risk programme without the sprawl.",
  works: "Add automated assessments, delivery and expert review hours.",
  ultimate: "Full-service: training, co-pilot advisory and a dedicated line.",
};

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [annual, setAnnual] = useState(true);
  const [checkoutPlan, setCheckoutPlan] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);

  useEffect(() => {
    api.get("/pricing/plans").then((r) => setPlans(r.data.plans)).catch((e) => toast.error(apiError(e)));
  }, []);

  // SEO: per-route title, meta description + canonical.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Pricing — RiskEye · Digital risk register & M365 assessment from $10/user/mo";
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content");
    desc?.setAttribute("content", "RiskEye pricing: Starter $10/user/mo, The Works $20/user/mo (billed annually), Ultimate custom. Digital risk register + Microsoft 365 CIS assessment + action tracking. 3-licence minimum. Stripe checkout.");
    const link = document.querySelector('link[rel="canonical"]');
    const prevHref = link?.getAttribute("href");
    link?.setAttribute("href", "https://riskeye.io/pricing");
    return () => {
      document.title = prevTitle;
      if (desc && prevDesc != null) desc.setAttribute("content", prevDesc);
      if (link && prevHref != null) link.setAttribute("href", prevHref);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#0A1329] text-white" data-testid="pricing-page">
      {/* Top nav — matches Landing */}
      <header className="sticky top-0 z-30 backdrop-blur-md bg-[#0A1329]/85 border-b border-white/5">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-brand">
            <span className="h-9 w-9 rounded-xl bg-riskeye-cyan flex items-center justify-center text-[#0A1329] font-black text-lg">R</span>
            <span className="font-display font-bold text-white">RiskEye</span>
          </Link>
          <div className="hidden md:flex items-center gap-7 text-[13px] text-slate-300 font-medium">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <Link to="/trust" className="hover:text-white transition-colors">Trust</Link>
            <Link to="/pricing" className="text-white">Pricing</Link>
            <Link to="/how-to-use" className="hover:text-white transition-colors">How to use</Link>
          </div>
          <Link to="/login" className="text-[13px] font-medium text-slate-300 hover:text-white px-3 py-2" data-testid="nav-signin">Sign in →</Link>
        </nav>
      </header>

      {/* Hero */}
      <section className="relative pt-20 pb-10 text-center overflow-hidden">
        <div className="absolute top-10 left-1/2 -translate-x-1/2 w-[560px] h-[560px] rounded-full bg-cyan-500/10 blur-3xl pointer-events-none" aria-hidden />
        <div className="relative max-w-3xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-riskeye-cyan border border-cyan-500/30 bg-cyan-500/10 rounded-full px-3 py-1 mb-6">
            Pricing · Simple seat-based
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight">
            Pick the plan that fits<br />
            <span className="text-riskeye-cyan">how you work.</span>
          </h1>
          <p className="mt-5 text-slate-300 text-lg leading-relaxed">
            Per-seat pricing in USD. Minimum licence counts apply. Switch billing at any time; cancel at the end of the term.
          </p>

          {/* Billing toggle */}
          <div className="mt-8 inline-flex items-center bg-white/5 border border-white/10 rounded-full p-1" data-testid="billing-toggle">
            <button
              type="button"
              onClick={() => setAnnual(true)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${annual ? "bg-riskeye-cyan text-[#0A1329]" : "text-slate-300 hover:text-white"}`}
              data-testid="billing-annual"
            >
              Annual <span className="text-[11px] font-mono ml-1 opacity-80">· save up to 50%</span>
            </button>
            <button
              type="button"
              onClick={() => setAnnual(false)}
              className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${!annual ? "bg-riskeye-cyan text-[#0A1329]" : "text-slate-300 hover:text-white"}`}
              data-testid="billing-monthly"
            >
              Monthly
            </button>
          </div>
        </div>
      </section>

      {/* Plan cards */}
      <section className="relative pb-24 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
          {plans.map((p, i) => {
            const Icon = PLAN_ICON[p.id] || Shield;
            const featured = p.id === "works";
            const contactOnly = p.contact_only;
            const price = annual ? p.annual_price : p.monthly_price;
            return (
              <div
                key={p.id}
                className={`relative rounded-2xl p-7 flex flex-col ${
                  featured
                    ? "bg-gradient-to-br from-[#0F1D45] to-[#111E42] border-2 border-cyan-400/60 shadow-[0_0_40px_rgba(34,211,238,0.15)]"
                    : "bg-[#0F1A38] border border-white/10"
                }`}
                data-testid={`plan-${p.id}`}
              >
                {featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-riskeye-cyan text-[#0A1329] text-[10px] font-mono uppercase tracking-widest font-bold px-3 py-1 rounded-full">
                    Most popular
                  </div>
                )}
                <div className="flex items-center gap-2.5 mb-4">
                  <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${featured ? "bg-riskeye-cyan text-[#0A1329]" : "bg-white/10 text-cyan-300 border border-white/15"}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest font-mono text-cyan-300 font-semibold">Package {i + 1}</div>
                    <div className="font-display font-bold text-xl text-white">{p.name}</div>
                  </div>
                </div>
                <p className="text-sm text-slate-300 leading-relaxed min-h-[42px]">{PLAN_TAGLINE[p.id]}</p>

                {/* Price */}
                <div className="mt-5 pb-5 border-b border-white/10">
                  {contactOnly ? (
                    <div>
                      <div className="font-display font-extrabold text-4xl text-white">Custom</div>
                      <div className="text-sm text-slate-400 mt-1">Contact us for a tailored quote.</div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-baseline gap-1.5">
                        <span className="text-slate-400 text-xl">$</span>
                        <span className="font-display font-extrabold text-5xl text-white tabular-nums">{price}</span>
                        <span className="text-slate-400 text-sm">USD / user / month</span>
                      </div>
                      <div className="text-xs text-slate-400 mt-1.5">
                        {annual
                          ? `Billed annually · $${price * 12} per user/year`
                          : `Billed monthly · cancel anytime`}
                      </div>
                    </div>
                  )}
                  <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest text-amber-200 bg-amber-500/10 border border-amber-400/20 rounded-full px-2.5 py-1">
                    Min {p.min_seats} licences
                  </div>
                </div>

                {/* Features */}
                <ul className="mt-5 space-y-2.5 flex-1">
                  {p.modules.map((m, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-200">
                      <Check className={`h-4 w-4 mt-0.5 shrink-0 ${featured ? "text-riskeye-cyan" : "text-emerald-400"}`} />
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <div className="mt-6">
                  {contactOnly ? (
                    <Button
                      onClick={() => setContactOpen(true)}
                      className="w-full bg-white text-[#0A1329] hover:bg-slate-100 font-semibold h-12"
                      data-testid={`plan-cta-${p.id}`}
                    >
                      Contact sales
                      <ArrowRight className="h-4 w-4 ml-1.5" />
                    </Button>
                  ) : (
                    <Button
                      onClick={() => setCheckoutPlan(p)}
                      className={`w-full h-12 font-semibold ${
                        featured
                          ? "bg-riskeye-cyan text-[#0A1329] hover:bg-cyan-300"
                          : "bg-white/10 border border-white/15 text-white hover:bg-white/20"
                      }`}
                      data-testid={`plan-cta-${p.id}`}
                    >
                      Buy {p.name}
                      <ArrowRight className="h-4 w-4 ml-1.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Reassurance strip */}
        <div className="max-w-5xl mx-auto mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
          {[
            { h: "No access until onboarded", s: "Buying a licence here captures your order. Our team contacts you to provision access securely." },
            { h: "Test mode available", s: "We'll run your first purchase in Stripe test mode until go-live is confirmed." },
            { h: "VAT / Tax handled by Stripe", s: "Stripe calculates and collects tax at checkout where applicable." },
          ].map((x) => (
            <div key={x.h} className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
              <div className="font-display font-semibold text-white text-sm">{x.h}</div>
              <div className="text-xs text-slate-400 mt-1.5 leading-relaxed">{x.s}</div>
            </div>
          ))}
        </div>

        {/* Compare all features */}
        <div className="max-w-6xl mx-auto mt-14">
          <button
            type="button"
            onClick={() => setCompareOpen((v) => !v)}
            className="w-full flex items-center justify-between rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] px-5 py-4 transition-colors group"
            data-testid="compare-toggle"
            aria-expanded={compareOpen}
          >
            <div className="text-left">
              <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-300 font-semibold">Side-by-side</div>
              <div className="font-display font-semibold text-white text-lg">Compare all features</div>
            </div>
            <div className="flex items-center gap-2 text-slate-300 text-sm font-medium">
              <span className="hidden sm:inline">{compareOpen ? "Hide" : "Show"} table</span>
              {compareOpen ? <ChevronUp className="h-5 w-5 group-hover:translate-y-[-1px] transition-transform" /> : <ChevronDown className="h-5 w-5 group-hover:translate-y-[1px] transition-transform" />}
            </div>
          </button>

          {compareOpen && <CompareTable plans={plans} annual={annual} onBuy={setCheckoutPlan} onContact={() => setContactOpen(true)} />}
        </div>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto mt-20" data-testid="pricing-faq">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.18em] text-cyan-300 border border-cyan-500/30 bg-cyan-500/10 rounded-full px-3 py-1 mb-4">
              Before you ask
            </div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-white tracking-tight">
              Questions, answered.
            </h2>
            <p className="mt-3 text-slate-400 text-sm leading-relaxed">
              Six things people ask most before they buy. If yours isn't here, reply to any email we send — a human reads it.
            </p>
          </div>

          <Accordion
            type="single"
            collapsible
            className="rounded-2xl border border-white/10 bg-[#0F1A38]/60 divide-y divide-white/5 overflow-hidden"
          >
            {FAQ_ITEMS.map((item, idx) => (
              <AccordionItem
                key={idx}
                value={`faq-${idx}`}
                className="border-0 px-5 sm:px-6"
                data-testid={`faq-item-${idx}`}
              >
                <AccordionTrigger className="hover:no-underline py-5 text-left font-display font-semibold text-white text-base sm:text-[17px] [&>svg]:text-cyan-300 [&>svg]:h-5 [&>svg]:w-5">
                  <span className="flex items-start gap-3 pr-4">
                    <span className="text-[11px] font-mono text-cyan-300/70 tabular-nums mt-1">0{idx + 1}</span>
                    <span>{item.q}</span>
                  </span>
                </AccordionTrigger>
                <AccordionContent className="text-slate-300 text-sm leading-relaxed pb-5 pl-9 pr-2">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-6 text-[12.5px] text-slate-400">
            Still unsure? Email <a href="mailto:sales@riskeye.io" className="text-cyan-300 hover:underline">sales@riskeye.io</a> and we'll come back within one business day.
          </div>
        </div>
      </section>

      {checkoutPlan && (
        <CheckoutDialog
          plan={checkoutPlan}
          annual={annual}
          onClose={() => setCheckoutPlan(null)}
        />
      )}
      {contactOpen && (
        <ContactSalesDialog onClose={() => setContactOpen(false)} />
      )}

      <footer className="border-t border-white/5 py-10 text-center text-slate-400 text-sm">
        © RiskEye. Pricing shown in USD. Need help? <Link to="/trust" className="text-cyan-300 hover:underline">Trust centre</Link>.
      </footer>
    </div>
  );
}

function CheckoutDialog({ plan, annual, onClose }) {
  const [qty, setQty] = useState(plan.min_seats);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);
  const lookupKey = annual ? plan.annual_lookup_key : plan.monthly_lookup_key;
  const unit = annual ? plan.annual_price : plan.monthly_price;
  const perSeatPerYear = annual ? unit * 12 : unit * 12;
  const total = annual ? qty * unit * 12 : qty * unit;

  const canSubmit = qty >= plan.min_seats && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const buy = async () => {
    setBusy(true);
    try {
      const r = await api.post("/payments/checkout", {
        lookup_key: lookupKey,
        quantity: qty,
        origin_url: window.location.origin,
        plan_id: plan.id,
        buyer_email: email,
        buyer_name: name || null,
        company: company || null,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(apiError(e));
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="checkout-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Checkout · {plan.name}</DialogTitle>
          <DialogDescription>
            {annual ? `Billed annually at $${unit}/user/month ($${perSeatPerYear}/user/year).` : `Billed monthly at $${unit}/user/month.`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Number of licences</Label>
            <div className="flex items-center gap-2 mt-1">
              <Button type="button" variant="outline" size="icon" className="h-10 w-10" onClick={() => setQty(Math.max(plan.min_seats, qty - 1))} data-testid="qty-dec"><Minus className="h-4 w-4" /></Button>
              <Input type="number" min={plan.min_seats} value={qty} onChange={(e) => setQty(Math.max(plan.min_seats, Number(e.target.value) || plan.min_seats))} className="text-center font-mono tabular-nums font-bold text-lg h-10" data-testid="qty-input" />
              <Button type="button" variant="outline" size="icon" className="h-10 w-10" onClick={() => setQty(qty + 1)} data-testid="qty-inc"><PlusIcon className="h-4 w-4" /></Button>
            </div>
            <div className="text-[11px] text-slate-500 mt-1">Minimum {plan.min_seats} licences.</div>
          </div>
          <div>
            <Label>Work email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" data-testid="buyer-email" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Your name (optional)</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="buyer-name" />
            </div>
            <div>
              <Label>Company (optional)</Label>
              <Input value={company} onChange={(e) => setCompany(e.target.value)} data-testid="buyer-company" />
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-3.5 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold">Total today</div>
              <div className="font-display font-extrabold text-2xl text-slate-900 tabular-nums">${total.toLocaleString()}</div>
              <div className="text-[11px] text-slate-500">{annual ? "USD · billed annually" : "USD · first month"}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-widest font-mono text-slate-500 font-semibold">Per seat</div>
              <div className="font-mono font-semibold text-slate-800">${unit}/mo</div>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button disabled={!canSubmit || busy} onClick={buy} className="bg-[#0A1329] hover:bg-[#162347] text-white" data-testid="checkout-confirm">
            {busy ? "Redirecting…" : "Continue to Stripe"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* Compare-all-features table — source of truth for which plan includes what. */
const COMPARE_GROUPS = [
  {
    group: "Core risk management",
    rows: [
      { f: "Digital Risk Register (5×5 scoring, versions)", starter: true, works: true, ultimate: true },
      { f: "Findings (track, assign, close)",               starter: true, works: true, ultimate: true },
      { f: "Risk Acceptance workflow",                      starter: true, works: true, ultimate: true },
      { f: "Risk Appetite (per-category + financial caps)", starter: true, works: true, ultimate: true },
      { f: "Executive reporting (PDF export)",              starter: true, works: true, ultimate: true },
    ],
  },
  {
    group: "Platform & security",
    rows: [
      { f: "Multi-tenant RBAC (Admin · Approver · Viewer)", starter: true, works: true, ultimate: true },
      { f: "Immutable audit trail (hash-chained)",          starter: true, works: true, ultimate: true },
      { f: "Dashboard & heatmap analytics",                 starter: true, works: true, ultimate: true },
      { f: "Automated encrypted S3 backups",                starter: true, works: true, ultimate: true },
      { f: "Product roadmap voting",                        starter: true, works: true, ultimate: true },
    ],
  },
  {
    group: "Automation & assessments",
    rows: [
      { f: "M365 Assessment (CIS v8 scanning)",             starter: false, works: true, ultimate: true },
      { f: "Projects (Kanban, Gantt, RAG, dependencies)",   starter: false, works: true, ultimate: true },
      { f: "Advisor Notes (AI-assisted guidance)",          starter: false, works: true, ultimate: true },
    ],
  },
  {
    group: "Expert support",
    rows: [
      { f: "Email + in-app support",                        starter: true, works: true, ultimate: true },
      { f: "Risk Advisor sessions",                         starter: false, works: "2 × 30 min / month", ultimate: "4 × 60 min / month" },
      { f: "Platform training",                             starter: false, works: false, ultimate: true },
      { f: "Dedicated customer success manager",            starter: false, works: false, ultimate: true },
      { f: "Priority onboarding (white-glove)",             starter: false, works: false, ultimate: true },
    ],
  },
  {
    group: "Commercial",
    rows: [
      { f: "Minimum licences",       starter: "3", works: "3", ultimate: "10" },
      { f: "Billing cadence",        starter: "Monthly or Annual", works: "Monthly or Annual", ultimate: "Annual only" },
      { f: "Price per user / month", starter: "$10 (annual) or $20 (monthly)", works: "$20 (annual) or $30 (monthly)", ultimate: "Custom" },
    ],
  },
];

function CompareCell({ value }) {
  if (value === true) return <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-emerald-500/15 border border-emerald-400/30"><Check className="h-3.5 w-3.5 text-emerald-300" /></span>;
  if (value === false) return <span className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-white/5 border border-white/10"><XIcon className="h-3.5 w-3.5 text-slate-500" /></span>;
  return <span className="text-[12.5px] text-slate-100 font-medium leading-tight">{value}</span>;
}

function CompareTable({ plans, annual, onBuy, onContact }) {
  const starter  = plans.find((p) => p.id === "starter");
  const works    = plans.find((p) => p.id === "works");
  const ultimate = plans.find((p) => p.id === "ultimate");
  return (
    <div className="mt-5 rounded-2xl border border-white/10 bg-[#0F1A38]/60 overflow-hidden" data-testid="compare-table">
      <div className="hidden md:grid grid-cols-[1.6fr_1fr_1fr_1fr] gap-3 px-6 py-4 border-b border-white/10 bg-white/[0.02] text-[10.5px] font-mono uppercase tracking-widest text-slate-400 font-semibold">
        <div>Feature</div>
        <div className="text-center">Starter</div>
        <div className="text-center">
          The Works
          <span className="ml-1.5 inline-flex items-center text-[8.5px] bg-riskeye-cyan text-[#0A1329] rounded-full px-1.5 py-0.5 align-middle">POPULAR</span>
        </div>
        <div className="text-center">Ultimate</div>
      </div>

      {COMPARE_GROUPS.map((grp) => (
        <div key={grp.group}>
          <div className="px-6 pt-5 pb-2 text-[10.5px] font-mono uppercase tracking-widest text-cyan-300 font-semibold" data-testid={`compare-group-${grp.group.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{grp.group}</div>
          <div className="divide-y divide-white/5">
            {grp.rows.map((row, idx) => (
              <div key={`${grp.group}-${idx}`} className="grid grid-cols-1 md:grid-cols-[1.6fr_1fr_1fr_1fr] gap-3 px-6 py-3 items-center hover:bg-white/[0.025] transition-colors">
                <div className="text-sm text-slate-200 font-medium">{row.f}</div>
                <div className="md:text-center flex md:block items-center gap-2">
                  <span className="md:hidden text-[10.5px] font-mono uppercase tracking-widest text-slate-500 w-20 shrink-0">Starter</span>
                  <CompareCell value={row.starter} />
                </div>
                <div className="md:text-center flex md:block items-center gap-2">
                  <span className="md:hidden text-[10.5px] font-mono uppercase tracking-widest text-slate-500 w-20 shrink-0">The Works</span>
                  <CompareCell value={row.works} />
                </div>
                <div className="md:text-center flex md:block items-center gap-2">
                  <span className="md:hidden text-[10.5px] font-mono uppercase tracking-widest text-slate-500 w-20 shrink-0">Ultimate</span>
                  <CompareCell value={row.ultimate} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <div className="grid grid-cols-1 md:grid-cols-[1.6fr_1fr_1fr_1fr] gap-3 px-6 py-5 border-t border-white/10 bg-white/[0.02]">
        <div className="hidden md:block" />
        <div className="md:text-center">
          {starter && <Button onClick={() => onBuy(starter)} className="w-full md:w-auto bg-white/10 border border-white/15 text-white hover:bg-white/20" data-testid="compare-cta-starter">Buy Starter</Button>}
        </div>
        <div className="md:text-center">
          {works && <Button onClick={() => onBuy(works)} className="w-full md:w-auto bg-riskeye-cyan text-[#0A1329] hover:bg-cyan-300 font-semibold" data-testid="compare-cta-works">Buy The Works</Button>}
        </div>
        <div className="md:text-center">
          {ultimate && <Button onClick={onContact} className="w-full md:w-auto bg-white text-[#0A1329] hover:bg-slate-100 font-semibold" data-testid="compare-cta-ultimate">Contact sales</Button>}
        </div>
      </div>
    </div>
  );
}

function ContactSalesDialog({ onClose }) {
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="contact-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Talk to us about Ultimate</DialogTitle>
          <DialogDescription>
            Ultimate is quoted per customer. Share a quick note and we'll come back within one business day.
          </DialogDescription>
        </DialogHeader>
        <div className="text-sm text-slate-700 leading-relaxed py-2">
          Email <a className="text-cyan-700 font-semibold underline" href="mailto:sales@riskeye.io?subject=Ultimate%20plan%20enquiry">sales@riskeye.io</a> or
          book 30 minutes via <a className="text-cyan-700 font-semibold underline" target="_blank" rel="noreferrer" href="https://calendly.com/sequrenz/30min">calendly.com/sequrenz/30min</a>.
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="bg-[#0A1329] hover:bg-[#162347] text-white">Got it</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
